import { Box, Paper, Divider } from "@mui/material";
import CelleraLogo from "components/common/CelleraLogo";
import { EPath } from "constants/path";
import { useToast } from "contexts/ToastContext";
import { useAuth } from "contexts/AuthContext";
import { useState, useEffect, useRef } from "react";

import GoogleSignIn from "../components/features/auth/GoogleSignIn";
import LoginEmailPasswordForm from "../components/features/auth/LoginEmailPasswordForm";
import { GoogleSignInRequestUserTypeEnum } from "../libs/cellera-sdk";
import { AuthResult } from "../types";

export default function LoginPage() {
  const [rememberMe, setRememberMe] = useState(false);
  const { showError } = useToast();
  const { user, loading } = useAuth();
  const hasHandledRedirectRef = useRef(false);
  const wasUnauthenticatedOnMountRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (wasUnauthenticatedOnMountRef.current === null && !loading) {
      wasUnauthenticatedOnMountRef.current = !user;
    }
  }, [loading, user]);

  // Handle navigation after Google redirect sign-in
  // Password and popup logins navigate immediately via handleAuthSuccess
  useEffect(() => {
    if (loading) {
      return; // Still loading, wait
    }

    // Handle redirect flow: check if user just became authenticated
    const justSignedIn = wasUnauthenticatedOnMountRef.current === true && user;

    if (justSignedIn && !hasHandledRedirectRef.current) {
      // Check if this is a Google user (indicates Google login)
      const isGoogleUser = user.providerData.some(
        (provider) => provider.providerId === "google.com",
      );

      // Navigate if it's a Google user or if we have OAuth callback parameters
      const urlParams = new URLSearchParams(window.location.search);
      const hasGoogleCallback = urlParams.has("code") || urlParams.has("state");

      if (isGoogleUser || hasGoogleCallback) {
        hasHandledRedirectRef.current = true;

        // Clean up URL parameters if present
        if (hasGoogleCallback) {
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname,
          );
        }

        // Navigate to home page
        window.location.href = "/";
      }
    }
  }, [user, loading]);

  const handleAuthSuccess: (result: AuthResult) => void = (result) => {
    if (
      "requiresEmailVerification" in result &&
      result.requiresEmailVerification
    ) {
      // Handle email verification required case
      return;
    }
    if ("requiresOnboarding" in result && result.requiresOnboarding) {
      // Handle onboarding required case
      window.location.href = EPath.HOME;
      return;
    } else {
      // For password login and Google popup login, navigate immediately
      // The useEffect will handle Google redirect flows
      window.location.href = "/";
    }
  };

  const handleAuthError = (error: Error) => {
    // Handle authentication error
    showError("Authentication failed", error.message);
  };

  return (
    <Box className="h-screen bg-color_F8F8F8 flex items-center justify-center p-4">
      <Paper
        elevation={1}
        className="w-full max-w-xl p-16 rounded-xl shadow-lg"
        sx={{
          backgroundColor: "white",
          borderRadius: "12px",
        }}
      >
        {/* Logo */}
        <Box className="text-center flex items-center justify-center mb-8">
          <CelleraLogo />
        </Box>

        {/* Login with Email and Password form */}
        <LoginEmailPasswordForm
          userType={GoogleSignInRequestUserTypeEnum.Admin}
          onSuccess={handleAuthSuccess}
          onError={handleAuthError}
          rememberMe={rememberMe}
          setRememberMe={setRememberMe}
        />

        {/* Divider */}
        <Divider className="my-6" />

        {/* Google Sign In */}
        <GoogleSignIn
          userType={GoogleSignInRequestUserTypeEnum.Admin}
          onSuccess={handleAuthSuccess}
          onError={handleAuthError}
          rememberMe={rememberMe}
        />
      </Paper>
    </Box>
  );
}
